import * as _ from "underscore";
import * as React from "react";
import * as moment from "moment";
import { connect } from "react-redux";
import { Button } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import { Tabs, Tab } from "react-bootstrap";
import { cloneDeep } from "lodash";

import APIClient from "web_modules/APIClient";
import DocEntry from "web_modules/underwriter_dashboard/shared/DocEntry";
import { EmploymentShape, DocTypes } from "@eave/shared/types/api";
import {
  appToBorrower1Name,
  appToBorrower2Name,
  findFieldValue,
  findFieldValueForSection,
} from "web_modules/underwriter_dashboard/shared";
import { loadApp } from "web_modules/underwriter_dashboard/actions";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import EmploymentPanel from "./EmploymentPanel";
import OtherIncomePanel from "./OtherIncomePanel";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

require(`../../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/\
income_documents_review.scss`);

/* IncomeDocumentsReviewHurdleEditor */
interface HurdleEditorProps extends HurdleProps {
  dispatch?: Function;
}

class IncomeDocumentsReviewHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props) {
    super(props);

    const rentals = this.rentalProperties(props.app.owned_real_estate);

    this.state = {
      selectedDocId: null,
      additionalIncomes: props.app.additional_incomes,
      ownedRealEstate: rentals,
      ownedRealEstateIDsToScheduleEPropertyLabels: rentals.reduce((acc, rental) => {
        acc[rental.id] = rental.schedule_e_property_labels.reduce((labels, label) => {
          labels[label.document_id] = label.property_label;
          return labels;
        }, {});
        return acc;
      }, {}),
      employmentIDsToEINs: props.app.employments.reduce((acc, employment) => {
        acc[employment.id] = employment.ein;
        return acc;
      }, {}),
    };

    this.handleChange = this.handleChange.bind(this);
    this.isPrimary = this.isPrimary.bind(this);
    this.setOwnedRealEstateScheduleEPropertyLabel = this.setOwnedRealEstateScheduleEPropertyLabel.bind(
      this,
    );
    this.propertyLabelAndAddressOptions = this.propertyLabelAndAddressOptions.bind(this);
    this.propertyLabelAndAddressOptionSelected = this.propertyLabelAndAddressOptionSelected.bind(
      this,
    );
  }

  isPrimary = employment =>
    (employment.borrower_entity === 1 &&
      employment.id === this.props.app.borrower1_primary_income_id &&
      (this.props.app.borrower1_primary_income_source === "salaried" ||
        this.props.app.borrower1_primary_income_source === "selfEmployed")) ||
    (employment.borrower_entity === 2 &&
      employment.id === this.props.app.borrower2_primary_income_id &&
      (this.props.app.borrower2_primary_income_source === "salaried" ||
        this.props.app.borrower2_primary_income_source === "selfEmployed"));

  rentalProperties(ownedRealEstate) {
    return _.filter(
      ownedRealEstate,
      (property: any) =>
        property.operator_ownership_status === "RentedOut" ||
        property.ownership_status === "RentedOut",
    );
  }

  documentsOfType(type, documents = null) {
    const docs = documents || this.props.app.documents;
    return _.filter(docs, (document: any) => document.type === type);
  }

  documentsForEmployment(employmentID, documents = null) {
    const docs = documents || this.props.app.documents;
    return _.filter(
      docs,
      (document: any) =>
        document.owner_entity_type === "employment" && document.owner_entity_id === employmentID,
    );
  }

  einsAndBusinessNamesFromDocument(document: any, sections: string[]) {
    return document.sections.reduce((acc, section) => {
      const ein = findFieldValueForSection(document, "ein", section.section_key);
      const businessName = findFieldValueForSection(document, "business_name", section.section_key);
      if (ein !== "") {
        acc[ein] = businessName;
      }
      return acc;
    }, {});
  }

  einsAndBusinessNamesFromTaxDocuments() {
    const { documents } = this.props.app;

    const taxReturns = _.filter(documents, (document: any) =>
      document.type.startsWith("income_tax_return_"),
    );

    const taxReturnSectionsWithEINsAndBusinessNames = [
      "1065",
      "1120s",
      "k1_1065",
      "k1_1120s",
      "schedule_c",
    ];

    const pAndLs = _.filter(
      documents,
      (document: any) => document.type === DocTypes.ProfitAndLossStatement,
    );

    const pAndLSectionsWithEINsAndBusinessNames = ["main"];

    let einsToBusinessNames = {};

    taxReturns.forEach(tr => {
      einsToBusinessNames = {
        ...einsToBusinessNames,
        ...this.einsAndBusinessNamesFromDocument(tr, taxReturnSectionsWithEINsAndBusinessNames),
      };
    });

    pAndLs.forEach(pAndL => {
      einsToBusinessNames = {
        ...einsToBusinessNames,
        ...this.einsAndBusinessNamesFromDocument(pAndL, pAndLSectionsWithEINsAndBusinessNames),
      };
    });

    return einsToBusinessNames;
  }

  propertyLabelAndAddressFromScheduleE(taxReturnDocument: any) {
    // A, B, and C are the labels of properties on schedule E
    const propertyLabels = ["A", "B", "C"];
    return propertyLabels.reduce(
      (acc, label) => ({
        ...acc,
        [label]: findFieldValueForSection(
          taxReturnDocument,
          `property_${label.toLowerCase()}`,
          "schedule_e",
        ),
      }),
      {},
    );
  }

  propertyLabelAndAddressOptions(taxReturnDocument) {
    const propertyLabelAndAddress = this.propertyLabelAndAddressFromScheduleE(taxReturnDocument);

    const options = Object.keys(propertyLabelAndAddress).map(propertyLabel => {
      const address = propertyLabelAndAddress[propertyLabel];
      const label = address
        ? `Property ${propertyLabel} - ${address}`
        : `Property ${propertyLabel}`;

      return {
        label,
        value: propertyLabel,
      };
    });

    options.push({
      label: "Does not appear on this tax return",
      value: "NA", // null is taken by react-select as no option selected!
      // here, operator has chosen that it doesn't appear on the tax return.
      // specify that choice, rather than leaving as unselected, so that operator
      // coming back here, say for a 2nd time, knows that matching the REO to the schedule E
      // has already been addressed (and in this case, there is no match).
    });

    return options;
  }

  // react-select option (value, label) for the property label of the reo on the tax return
  propertyLabelAndAddressOptionSelected(ownedRealEstateID, taxReturnDocument) {
    const { ownedRealEstateIDsToScheduleEPropertyLabels } = this.state;

    // get the options again (below) -- can't only use state, since state stores
    // only the property label (not the address hint, which is part of what is displayed
    // as `property label - address` for human matching)
    const selected = this.propertyLabelAndAddressOptions(taxReturnDocument).filter(
      option =>
        option.value ===
        ownedRealEstateIDsToScheduleEPropertyLabels[ownedRealEstateID][taxReturnDocument.id],
    );

    return selected.length > 0 ? selected[0] : null;
  }

  setOwnedRealEstateScheduleEPropertyLabel(ownedRealEstateID, taxReturnDocumentID, e) {
    // set to one of {A, B, C, NA, ""}
    // operator xs out the current option -> react-select sends e === null -> translated here to ""
    const scheduleEPropertyLabel = e ? e.value : "";
    const ownedRealEstateIDsToScheduleEPropertyLabels = cloneDeep(
      this.state.ownedRealEstateIDsToScheduleEPropertyLabels,
    );

    // indicate that a given reo is on a given schedule E with a certain property label
    // eg {
    //   some reo id 1: {
    //     some tax return id 10: "A",
    //     some tax return id 6: "C",
    //   },
    // }
    ownedRealEstateIDsToScheduleEPropertyLabels[ownedRealEstateID][
      taxReturnDocumentID
    ] = scheduleEPropertyLabel;

    this.setState({ ownedRealEstateIDsToScheduleEPropertyLabels });
  }

  einOptions(employment) {
    const einsToBusinessNames = this.einsAndBusinessNamesFromTaxDocuments();

    // also grab the one from the employment (in case it's not from the tax docs)
    if (employment && employment.ein) {
      einsToBusinessNames[employment.ein] = employment.name;
    }

    return Object.keys(einsToBusinessNames).map(ein => {
      const businessName = einsToBusinessNames[ein];
      const label = businessName ? `${ein} - ${businessName}` : ein;
      return {
        label,
        value: ein,
      };
    });
  }

  // value and label for the employment's ein
  einOptionSelected(employment) {
    const { employmentIDsToEINs } = this.state;

    // the original employment ein -- if it hasn't yet been altered here --
    // is not guaranteed to be one of the options from the tax docs, so einOptions adds it.
    // the selected option is the ein of the latest update to state for this employment
    const selected = this.einOptions(employment).filter(
      option => option.value === employmentIDsToEINs[employment.id],
    );

    // if ein is blank on the employment and no tax docs have been ingested with ein, return null
    return selected.length > 0 ? selected[0] : null;
  }

  borrowerEntityText(borrower_entity) {
    if (borrower_entity === 1) {
      return appToBorrower1Name(this.props.app);
    }
    if (borrower_entity === 2) {
      return appToBorrower2Name(this.props.app);
    }
    return "";
  }

  setSelectedDocId(docId) {
    this.setState({ selectedDocId: docId });
  }

  setEmploymentEIN(employmentID, e) {
    const employmentIDsToEINs = { ...this.state.employmentIDsToEINs };
    // x-ing out of the choice in the <Select /> is the operator saying
    // the ein for the employment should be empty (null -> "", below)
    const ein = e ? e.value : "";
    employmentIDsToEINs[employmentID] = ein;
    this.setState({ employmentIDsToEINs });
  }

  handleChange(incomeType, index, prop, value) {
    const newState = this.state;
    newState[incomeType][index][prop] = value;
    this.setState(newState);
  }

  updateOwnedRealEstateToScheduleEPropertyLabels() {
    const { ownedRealEstateIDsToScheduleEPropertyLabels } = this.state;

    return Object.keys(ownedRealEstateIDsToScheduleEPropertyLabels).map(ownedRealEstateID => {
      const scheduleEPropertyLabelsForREOID =
        ownedRealEstateIDsToScheduleEPropertyLabels[ownedRealEstateID];

      const translatedScheduleEPropertyLabels = Object.keys(scheduleEPropertyLabelsForREOID).map(
        documentID => ({
          document_id: +documentID,
          property_label: scheduleEPropertyLabelsForREOID[documentID],
        }),
      );

      return APIClient.put(
        `/uw/owned_real_estate/${ownedRealEstateID}/schedule_e_property_labels`,
        { updates: translatedScheduleEPropertyLabels },
      );
    });
  }

  updateEINsForEmployments() {
    const { employments, id } = this.props.app;
    const { employmentIDsToEINs } = this.state;

    return employments.map(employment => {
      const ein = employmentIDsToEINs[employment.id];
      employment.ein = ein;

      // yes, this is app id, as it's supposed to be, oddly
      return APIClient.put(`/uw/${id}/employments`, employment);
    });
  }

  onSave() {
    const { additionalIncomes, ownedRealEstate } = this.state;

    const promises = [
      ...additionalIncomes.map(additionalIncome => {
        APIClient.put(`/additional_income/${additionalIncome.id}`, additionalIncome);
      }),

      APIClient.put("/uw/owned_real_estate", { reos: ownedRealEstate }),

      ...this.updateEINsForEmployments(),

      ...this.updateOwnedRealEstateToScheduleEPropertyLabels(),
    ];

    Promise.all(promises).then(() => loadApp(this.props.dispatch, this.props.app.id));
  }

  render() {
    const { app, hurdle, onHide, hideTitle } = this.props;

    if (this.state.selectedDocId !== null) {
      return (
        <DocEntry
          {...this.props}
          docId={this.state.selectedDocId}
          docNotFoundMsg="Waiting for borrower(s) to upload their docs."
          modalTitle="Review document"
          onHide={this.setSelectedDocId.bind(this, null)}
        />
      );
    }

    const b1Employments = _.chain(this.props.app.employments)
      .filter((employment: EmploymentShape) => employment.borrower_entity === 1)
      .sortBy((employment: EmploymentShape) => !this.isPrimary(employment))
      .value();

    const b2Employments = _.chain(this.props.app.employments)
      .filter((employment: EmploymentShape) => employment.borrower_entity === 2)
      .sortBy((employment: EmploymentShape) => !this.isPrimary(employment))
      .value();

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide} hideTitle={hideTitle}>
        <div className="container-fluid">
          <Tabs className="tab-list" id="employmentTabs">
            <Tab eventKey={1} title="Borrower 1 Employments" tabClassName="income_doc_review_tab">
              {this.renderTabContent(b1Employments)}
            </Tab>
            {this.props.app.has_borrower2 && (
              <Tab eventKey={2} title="Borrower 2 Employments" tabClassName="income_doc_review_tab">
                {this.renderTabContent(b2Employments)}
              </Tab>
            )}
            <Tab eventKey={3} title="Other Income" tabClassName="income_doc_review_tab">
              {this.renderOtherIncome()}
            </Tab>
          </Tabs>
        </div>
        <footer className={styles.footer}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button kind="primary" onClick={this.onSave.bind(this)} renderIcon={Save16}>
            Save changes
          </Button>
        </footer>
      </HurdleView>
    );
  }

  renderOtherIncome() {
    return (
      <div className="container-fluid">
        <OtherIncomePanel
          additionalIncomes={this.state.additionalIncomes}
          setDocId={this.setSelectedDocId.bind(this)}
          allDocs={this.props.app.documents}
          ownedRealEstate={this.state.ownedRealEstate}
          setOwnedRealEstateScheduleEPropertyLabel={this.setOwnedRealEstateScheduleEPropertyLabel}
          propertyLabelAndAddressOptions={this.propertyLabelAndAddressOptions}
          propertyLabelAndAddressOptionSelected={this.propertyLabelAndAddressOptionSelected}
          handleChange={this.handleChange}
          loanApplicationID={this.props.app.id}
          hasCoborrower={this.props.app.has_borrower2}
        />
      </div>
    );
  }

  renderTabContent(employments) {
    if (!employments.length) {
      return <p>No employments found for this borrower.</p>;
    }
    return (
      <span>
        {employments.map((employment: EmploymentShape, i) => {
          const eDocs = this.documentsForEmployment(employment.id);
          let paystubs = this.documentsOfType(DocTypes.Paystub, eDocs);
          paystubs = _.sortBy(paystubs, (doc: any) =>
            moment(findFieldValue(doc, "pay_period_end")),
          );
          paystubs.reverse();
          let w2s = this.documentsOfType(DocTypes.W2, eDocs);
          w2s = _.sortBy(w2s, (doc: any) => -1 * Number(findFieldValue(doc, "year")));
          let wvoes = this.documentsOfType(DocTypes.WrittenVerificationOfEmployment, eDocs);
          wvoes = _.sortBy(wvoes, (doc: any) => moment(findFieldValue(doc, "ytd_pay_thru_date")));
          wvoes.reverse();
          const worknumbers = this.documentsOfType(
            DocTypes.WorknumberEmploymentVerification,
            eDocs,
          );

          return (
            <EmploymentPanel
              key={i}
              isPrimary={this.isPrimary(employment)}
              isCurrent={employment.end_date === null}
              setDocId={this.setSelectedDocId.bind(this)}
              employment={employment}
              einOptionSelected={this.einOptionSelected(employment)}
              einOptions={this.einOptions(employment)}
              setEmploymentEIN={this.setEmploymentEIN.bind(this)}
              borrowerName={this.borrowerEntityText(employment.borrower_entity)}
              w2s={w2s}
              paystubs={paystubs}
              wvoes={wvoes}
              worknumbers={worknumbers}
            />
          );
        })}
      </span>
    );
  }
}

export default connect()(IncomeDocumentsReviewHurdleEditor);
